import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { calculateStudyStreak } from '../utils/studyStreak';
import { useCourse } from '../contexts/CourseContext';
import DashboardTopbar from '../components/DashboardTopbar';
import SubjectPerformancePanel from '../components/SubjectPerformancePanel/SubjectPerformancePanel';
import { Email } from '../icons';
import './Profile.css';

const MONTHS_PT_BR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const MAX_CUSTOM_COURSES = 3;

const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatMemberSince = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return `${MONTHS_PT_BR[date.getMonth()]}/${date.getFullYear()}`;
};

const WEAK_SUBJECTS_LIMIT = 4;

function Profile() {
  const { activeCourseId, activeCourse, registeredCourses, deleteCustomCourse, resetCourseProgress, setActiveCourseId } = useCourse();
  const [loading, setLoading] = useState(true);
  const [studentName, setStudentName] = useState('Aluno');
  const [studentEmail, setStudentEmail] = useState('');
  const [memberSince, setMemberSince] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [studySessions, setStudySessions] = useState([]);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [courseToReset, setCourseToReset] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '/');

  const myCourses = registeredCourses || [];
  const customCoursesCount = myCourses.filter((course) => course.isCustom).length;

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;
    setSubmitting(true);
    setActionError('');
    try {
      await deleteCustomCourse(courseToDelete.id);
      setCourseToDelete(null);
    } catch (error) {
      setActionError(error.response?.data?.message || 'Erro ao excluir concurso. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmResetCourse = async () => {
    if (!courseToReset) return;
    setSubmitting(true);
    setActionError('');
    try {
      await resetCourseProgress(courseToReset.id);
      if (String(courseToReset.id) === activeCourseId) {
        setActiveCourseId('');
      }
      setCourseToReset(null);
    } catch (error) {
      setActionError(error.response?.data?.message || 'Erro ao resetar progresso do concurso. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${apiUrl}dashboard`, { params: { courseId: activeCourseId || undefined } })
      .then(({ data }) => {
        setStudentName(data.name || data.userInformation?.name || 'Aluno');
        setStudentEmail(data.userInformation?.email || '');
        setMemberSince(formatMemberSince(data.userInformation?.created_at));
        setStatistics(data.statistics);
        setStudySessions(data.studySessions || []);
      })
      .catch((error) => {
        console.error('Erro ao buscar os dados do perfil:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiUrl, activeCourseId]);

  const statsOverview = statistics?.overview || { totalRespondidas: 0, percentual: 0, simuladosAnalisados: 0 };
  const statsBySubject = statistics?.porMateria || [];
  const weakSubjects = statsBySubject
    .filter((item) => item.total > 0)
    .slice()
    .sort((a, b) => a.percentual - b.percentual)
    .slice(0, WEAK_SUBJECTS_LIMIT);

  const studyStreak = calculateStudyStreak(studySessions);
  const questoesRespondidasFormatted = statsOverview.totalRespondidas.toLocaleString('pt-BR');

  if (loading) {
    return (
      <div className="profile-page">
        <DashboardTopbar label="Perfil" />
        <div className="loading-message">Carregando seu perfil...</div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <DashboardTopbar label="Perfil" />

      <div className="profile-header-card">
        <div className="profile-header-info">
          <div className="profile-avatar-lg">{getInitials(studentName)}</div>
          <div className="profile-header-text">
            <span className="profile-name">{studentName}</span>
            <span className="profile-meta">
              {activeCourse?.name ? `Estudando para ${activeCourse.name}` : 'Nenhum concurso selecionado'}
              {memberSince ? ` · membro desde ${memberSince}` : ''}
            </span>
            {studentEmail && (
              <span className="profile-email">
                <Email style={{ fontSize: 14 }} />
                {studentEmail}
              </span>
            )}
            <div className="profile-badges">
              <span className="profile-streak-badge">
                🔥 {studyStreak} {studyStreak === 1 ? 'dia' : 'dias'} de sequência
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="kpi-strip profile-kpi-strip">
        <div className="kpi-card">
          <span className="kpi-label">QUESTÕES RESPONDIDAS</span>
          <span className="kpi-value">{questoesRespondidasFormatted}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">TAXA DE ACERTO</span>
          <span className="kpi-value">{statsOverview.percentual}%</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">SIMULADOS FEITOS</span>
          <span className="kpi-value">{statsOverview.simuladosAnalisados}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">SEQUÊNCIA DE ESTUDO</span>
          <span className="kpi-value">{studyStreak} {studyStreak === 1 ? 'dia' : 'dias'}</span>
        </div>
      </div>

      <div className="profile-section">
        <span className="quick-access-sidebar-label">Conquistas</span>
        <div className="profile-achievements-empty">
          <span className="profile-achievements-icon">🏆</span>
          <strong>Em breve</strong>
          <span className="profile-achievements-text">
            Estamos preparando badges e conquistas para recompensar sua dedicação nos estudos.
          </span>
        </div>
      </div>

      <SubjectPerformancePanel
        data={weakSubjects}
        title="Matérias que precisam de atenção"
        emptyMessage="Responda mais questões para identificar as matérias que precisam de atenção."
      />

      <div className="profile-section profile-danger-zone">
        <span className="quick-access-sidebar-label">Gerenciar meus concursos</span>
        {myCourses.length > 0 ? (
          <>
            <div className="profile-custom-courses-list">
              {myCourses.map((course) => (
                <div key={course.id} className="profile-custom-course-item">
                  <span className="profile-custom-course-info">
                    <span className="profile-custom-course-name">{course.name}</span>
                    <span className="profile-custom-course-badge">
                      {course.isCustom ? 'Personalizado' : 'Catálogo'}
                    </span>
                  </span>
                  {course.isCustom ? (
                    <button
                      type="button"
                      className="profile-custom-course-delete"
                      onClick={() => { setActionError(''); setCourseToDelete(course); }}
                    >
                      Excluir
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="profile-custom-course-delete"
                      onClick={() => { setActionError(''); setCourseToReset(course); }}
                    >
                      Resetar progresso
                    </button>
                  )}
                </div>
              ))}
            </div>
            <span className="profile-custom-courses-count">
              {customCoursesCount} de {MAX_CUSTOM_COURSES} concursos personalizados criados
            </span>
          </>
        ) : (
          <div className="profile-achievements-empty">
            <span className="profile-achievements-icon">📚</span>
            <strong>Nenhum concurso ainda</strong>
            <span className="profile-achievements-text">
              Você pode criar até {MAX_CUSTOM_COURSES} concursos personalizados com suas próprias matérias e tópicos.
            </span>
          </div>
        )}
      </div>

      {courseToDelete && (
        <div className="profile-delete-modal-backdrop">
          <div className="profile-delete-modal-card">
            <strong>Excluir "{courseToDelete.name}"?</strong>
            <span className="profile-delete-modal-warning">
              Essa ação é permanente. Você vai perder todas as matérias, tópicos, planos de estudo e
              simulados vinculados a este concurso, sem possibilidade de recuperação.
            </span>
            {actionError && <span className="profile-delete-modal-error">{actionError}</span>}
            <div className="profile-delete-modal-actions">
              <button
                type="button"
                className="profile-delete-modal-cancel"
                onClick={() => setCourseToDelete(null)}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="profile-delete-modal-confirm"
                onClick={confirmDeleteCourse}
                disabled={submitting}
              >
                {submitting ? 'Excluindo...' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {courseToReset && (
        <div className="profile-delete-modal-backdrop">
          <div className="profile-delete-modal-card">
            <strong>Resetar progresso em "{courseToReset.name}"?</strong>
            <span className="profile-delete-modal-warning">
              Essa ação é permanente. Como este é um concurso do catálogo, ele continua disponível para
              todos, mas você vai perder todo o seu progresso nele — respostas, simulados feitos, plano
              de estudo, sessões registradas e matérias/tópicos que você mesmo adicionou — sem
              possibilidade de recuperação. Ele deixa de aparecer como um dos seus concursos, e você
              poderá adicionar outro no lugar.
            </span>
            {actionError && <span className="profile-delete-modal-error">{actionError}</span>}
            <div className="profile-delete-modal-actions">
              <button
                type="button"
                className="profile-delete-modal-cancel"
                onClick={() => setCourseToReset(null)}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="profile-delete-modal-confirm"
                onClick={confirmResetCourse}
                disabled={submitting}
              >
                {submitting ? 'Resetando...' : 'Resetar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
